import { GridOptions } from '@ag-grid-community/core';
import AgGridSolid, { AgGridSolidRef } from '@ag-grid-community/solid';
import { Box, Button, Grid as MuiGrid, Stack } from '@atgfr8/atg-ui-tailwind';
import { useNavigate } from '@solidjs/router';
import { adminColDefs, capacityColDefs, colDefs } from '@store/claimsTab';
import { claimsStore } from '@store/claimsTab/store';
import { warningDataStore } from '@store/index';
import { userStore } from '@store/user';
import { formatDataTestId } from '@utils/dataTestIdFormatter';
import { routeOrderDetails } from '@utils/routeOrderDetails';
import { isAdmin, isCapacity } from '@utils/utils';
import { atgLogo } from '@views/ltl';
import { size } from 'lodash';

import './AgGridStyles.css';
import { getRowGroupStyle } from './utils';

export const ClaimsTabGrid = () => {
  const navigate = useNavigate();
  const gridOptions: GridOptions = {
    suppressScrollOnNewData: true,
    groupDefaultExpanded: 1,
    groupDisplayType: 'groupRows',
    defaultColDef: {
      flex: 1,
    },
    suppressDragLeaveHidesColumns: true,
    sideBar: {
      toolPanels: [
        {
          id: 'columns',
          labelDefault: 'Columns',
          labelKey: 'columns',
          iconKey: 'columns',
          toolPanel: 'agColumnsToolPanel',
        },
        {
          id: 'filters',
          labelDefault: 'Filters',
          labelKey: 'filters',
          iconKey: 'filter',
          toolPanel: 'agFiltersToolPanel',
        },
      ],
      defaultToolPanel: '',
    },
    onGridReady: (params) => {
      claimsStore.isLoading
        ? params.api.showLoadingOverlay()
        : params.api.hideOverlay();
    },
  };

  const updateSortModel = () => {
    const columnState = gridRef?.api.getColumnState();
    if (columnState) {
      const sortModel: { colId: string; sort: 'asc' | null }[] = [];

      columnState.forEach((col) => {
        if (typeof col.colId === 'string') {
          // Copy existing column state and update sort state
          sortModel.push({
            ...col,
            sort: (col.rowGroup ?? false) ? 'asc' : null,
          });
        }
      });

      gridRef?.api.applyColumnState({
        state: sortModel,
        applyOrder: false,
      });
    }
  };

  const onColumnRowGroupChanged = () => {
    updateSortModel();
  };

  let gridRef: AgGridSolidRef | undefined;
  return (
    <Stack class="h-full">
      <Box
        class="w-full p-2 border-gray-400 rounded-t-md border-b-0 border"
        backgroundColor="#F8F8F8"
      >
        <Button
          dataTestId={formatDataTestId('export-to-excel', 'button')}
          variant="outlined"
          size="sm"
          onClick={() => {
            if (gridRef) {
              gridRef.api.exportDataAsExcel();
            }
          }}
        >
          Export to Excel
        </Button>
      </Box>
      {size(Object.keys(userStore.user)) > 0 && (
        <MuiGrid
          class="ag-theme-alpine ag-theme-custom-border my-row-group-border custom-ag-row-icon-grid flex-1!"
          sx={{
            'min-height':
              warningDataStore.warningComponent.height > 0 ? '400px' : '',
          }}
        >
          <AgGridSolid
            ref={gridRef}
            overlayNoRowsTemplate="No data available"
            overlayLoadingTemplate={atgLogo}
            class="no-cell-grid"
            columnDefs={
              isAdmin()
                ? adminColDefs
                : isCapacity()
                  ? capacityColDefs
                  : colDefs
            }
            rowData={claimsStore.items}
            gridOptions={gridOptions}
            onColumnRowGroupChanged={onColumnRowGroupChanged}
            rowGroupPanelShow={'always'}
            getRowStyle={getRowGroupStyle}
            onRowClicked={(e) => {
              const orderId = (e.data as { orderId: number }).orderId;
              const isNative = routeOrderDetails(orderId);
              if (isNative) {
                navigate(`/order/details/${orderId}`);
              }
            }}
          />
        </MuiGrid>
      )}
    </Stack>
  );
};
